import { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useIsMutating } from 'react-query';

import { AppMessage } from '@/common/components';
import { FallbackedLayout } from '../../../../../common/layouts';
import { messageOnError } from '../../../../../common/utils';
import { HostingAccount } from '../../../entities';
import {
  HostingAccountsQueryKeys,
  useDeleteHostingAccountsMutation,
  useGetAllHostingAccountsQuery
} from '../../../queries';
import { HostingAccountsListView } from './HostingAccountsListView';

interface Props {
  hostingId: string;
}

export const HostingAccountsListContainer: FC<Props> = memo(({ hostingId }) => {
  const appMessageApi = AppMessage.useControls();
  const [dataSource, setDataSource] = useState<HostingAccount[]>([]);

  const {
    error: hostingAccountsError,
    isLoading: isHostingAccountsLoading,
    isFetching: isHostingAccountsFetching,
    isError: isHostingAccountsError,
    refetch: refetchHostingAccounts
  } = useGetAllHostingAccountsQuery(hostingId)({
    enabled: !!hostingId,
    onSuccess: (data) => {
      setDataSource(data);
    }
  });

  const { mutate: deleteHostingAccountMutation, isLoading: isDeleteHostingAccountFetching } =
    useDeleteHostingAccountsMutation(hostingId)({
      onSuccess: () => {
        appMessageApi.success('Акаунт успішно видалено!');
        refetchHostingAccounts();
      },
      onError: (err) => {
        messageOnError(err, appMessageApi);
      }
    });

  const isCreateAccountFetching = !!useIsMutating({
    mutationKey: [HostingAccountsQueryKeys.Create, hostingId],
    exact: true
  });

  const isEditAccountFetching = !!useIsMutating({
    mutationKey: [HostingAccountsQueryKeys.Edit, hostingId],
    exact: true
  });

  const handleDeleteAccount = useCallback((accountId: string): void => {
    deleteHostingAccountMutation(accountId);
  }, []);

  return (
    <FallbackedLayout
      isLoading={ isHostingAccountsLoading }
      isError={ isHostingAccountsError }
      error={ hostingAccountsError }
    >
      { (): ReactElement => (
        <HostingAccountsListView
          hostingAccountsDataSource={ dataSource }
          disableControlButtons={
            isCreateAccountFetching ||
             isDeleteHostingAccountFetching ||
             isEditAccountFetching ||
             isHostingAccountsFetching
          }
          handleDeleteAccount={ handleDeleteAccount }
        />
      ) }
    </FallbackedLayout>
  );
});
